import { type FC } from "react";
import { Card, CardContent, Stack, Box, CircularProgress } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import qs from "qs";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useCreatePlatformCustomCoverMutation,
  useCurrentPlatformQuery,
} from "@/partner/entities/platforms";
import { ErrorMessage } from "@/shared/components";
import { FilterProvider } from "@/partner/shared/components/Filter";
import { CustomCoversFilter } from "./CustomCoversFilter";
import { CustomCoversList } from "./CustomCoversList";
import { CustomCoverEditor } from "./CustomCoverEditor";

export const PlatformCustomCovers: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { tab, id: coverId } = qs.parse(searchParams.toString(), {
    ignoreQueryPrefix: true,
  }) as { tab?: string; id?: string };

  const { data: platformData, loading: platformLoading } =
    useCurrentPlatformQuery();
  const currentPlatform = platformData?.currentPlatform;

  const [createCover] = useCreatePlatformCustomCoverMutation({
    onCompleted: (data) => {
      enqueueSnackbar(t("platformCustomCovers.snackbar.created"), {
        variant: "success",
      });
      if (data.createPlatformCustomCover?.id) {
        searchParams.set("tab", "edit");
        searchParams.set("id", data.createPlatformCustomCover.id);
        setSearchParams(searchParams);
      }
    },
    onError: (error: { message: string }) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["PlatformCustomCovers"],
  });

  const handleCreate = async () => {
    if (!currentPlatform) {
      enqueueSnackbar(t("platformCustomCovers.snackbar.platformNotFound"), {
        variant: "error",
      });
      return;
    }

    await createCover({
      variables: {
        input: {},
      },
    });
  };

  const handleEdit = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  const handleCloseEditor = () => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams);
  };

  if (platformLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentPlatform) {
    return (
      <Card>
        <CardContent>
          <ErrorMessage error={new Error(t("platform.errors.noPlatform"))} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <FilterProvider>
        <Stack spacing={1}>
          <CustomCoversFilter onCreateCover={handleCreate} />
          <CustomCoversList onEditCover={handleEdit} />
        </Stack>
      </FilterProvider>

      {tab === "edit" && coverId && (
        <CustomCoverEditor
          coverId={coverId}
          open
          onClose={handleCloseEditor}
          onSave={handleCloseEditor}
        />
      )}
    </>
  );
};
